import { useEffect, useState } from 'react'
import { api, type Attestation, type Reconstruction } from '../api'
import { FundPicker, useFunds } from '../components/FundPicker'

/**
 * What has been signed off — TECHNICAL_DESIGN.md §9.4, §9.6.
 *
 * The other half of attestation, and the half that makes the first one worth
 * doing. Signing off produces a record: who, when, over how many rules, and a
 * digest of the exact resolved set. §9.6 prints that in the audit pack. Nothing
 * in the product showed it to the firm that made it — `GET
 * /portfolios/{id}/attestation` had no caller, and `attestation.reconstruct`
 * was not exposed over HTTP at all.
 *
 * So the claim could be handed to an auditor and never checked by anybody
 * inside. That is the wrong way round: a reconstruction that only an outsider
 * runs is a reconstruction the firm learns the result of from the outsider.
 *
 * **Re-resolving is offered per row, not run for every one.** It re-runs the
 * whole resolution at the recorded instants, which is real work, and the
 * question *does this still hold* is asked about a particular sign-off — the one
 * an auditor named, or the one before a change somebody is worried about. Run
 * automatically down a page it would be a spinner nobody reads.
 *
 * **A mismatch is a finding, not an error.** It means the historical answer has
 * moved, which is exactly the condition an append-only store exists to make
 * detectable rather than to promise away. So it is reported in those words
 * rather than as a failure.
 */
export function AttestHistory() {
  const { funds } = useFunds()
  const [fundId, setFundId] = useState('')
  const [rows, setRows] = useState<Attestation[]>([])
  const [checked, setChecked] = useState<Record<string, Reconstruction>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const selected = funds.find((f) => f.id === fundId) ?? null

  useEffect(() => {
    if (!fundId) {
      setRows([])
      return
    }
    let live = true
    setError(null)
    setChecked({})
    api
      .attestationHistory(fundId)
      .then((h) => live && setRows(h))
      .catch((e) => live && setError(e instanceof Error ? e.message : String(e)))
    return () => {
      live = false
    }
  }, [fundId])

  async function check(id: string) {
    setBusy(id)
    setError(null)
    try {
      setChecked((was) => ({ ...was, [id]: {} as Reconstruction }))
      const result = await api.attestationReconstruction(id)
      setChecked((was) => ({ ...was, [id]: result }))
    } catch (e) {
      setChecked((was) => {
        const next = { ...was }
        delete next[id]
        return next
      })
      setError(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(null)
    }
  }

  return (
    <div className="attest-history">
      <header className="page-head">
        <h2>What has been signed off</h2>
        <p className="note">
          Every attestation on record for a fund: who made it, when, and over how
          many limits. Each one can be re-resolved — the record holds the two
          instants and a digest of the exact set, so the system can say whether
          that set still comes out the same today.
        </p>
      </header>

      <div className="filters">
        <FundPicker
          label="Portfolio"
          value={fundId}
          onChange={setFundId}
          funds={funds}
          placeholder="Choose a fund"
          testId="fund"
        />
      </div>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {!selected ? (
        <p className="empty">Choose a fund to see its sign-off history.</p>
      ) : rows.length === 0 ? (
        <p className="empty" data-testid="never-attested">
          <code>{selected.code}</code> has never been signed off. Its limits are
          in force and nobody has stated that they are complete.
        </p>
      ) : (
        <table className="grid limits has-menus" data-testid="attestation-history">
          <thead>
            <tr>
              <th>Signed off</th>
              <th>By</th>
              <th>Over</th>
              <th>Note</th>
              <th>Still holds?</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const result = checked[row.id]
              return (
                <tr key={row.id} data-testid="attestation">
                  <td>
                    {new Date(row.attested_at).toLocaleString()}
                    <span className="clause">
                      as at {new Date(row.as_of).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    {/* §9.4 records whether the person who signed owns the fund.
                        Somebody else signing is permitted and is not the same
                        act, so the record keeps the difference. */}
                    <span className={row.by_owner ? 'clause' : 'warn'}>
                      {row.by_owner ? 'the fund’s owner' : 'not the fund’s owner'}
                    </span>
                  </td>
                  <td>
                    <span className="limit">{row.rule_count}</span>{' '}
                    <span className="clause">
                      {row.rule_count === 1 ? 'rule' : 'rules'}, {row.scope_count}{' '}
                      {row.scope_count === 1 ? 'group' : 'groups'}
                    </span>
                  </td>
                  <td className="clause">{row.note || '—'}</td>
                  <td>
                    {result === undefined ? (
                      <button
                        className="ghost"
                        disabled={busy !== null}
                        onClick={() => void check(row.id)}
                      >
                        Re-resolve
                      </button>
                    ) : busy === row.id ? (
                      <span className="clause">Re-resolving…</span>
                    ) : result.matches ? (
                      <span className="done" data-testid="reconstruction-ok">
                        Same {result.rule_count} rule(s) today
                      </span>
                    ) : (
                      /* Not an error. It means the historical answer has moved,
                         which is the condition an append-only store exists to
                         make detectable rather than to promise away. */
                      <span className="warn" data-testid="reconstruction-moved">
                        Resolves differently now — {result.rule_count} rule(s).
                        The set this was signed over is not the set that comes
                        out today.
                      </span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}
    </div>
  )
}
